import { getDesignImage } from './get-design-image';

type StaleDesignSource = {
	blueprint?: {
		thumbnail?: string;
		metadata?: { canonicalRenderingPath?: string; renderingCapturedAt?: string } | null;
	} | null;
	bpController?: { kitchen?: { lastMutatedAt?: number } | null } | null;
} | null | undefined;

export type IsRenderingStaleParams = {
	design: StaleDesignSource;
};

/**
 * True when the estimate/favorite/save flow should prompt to capture a rendering: no image set, or
 * the design was mutated AFTER the stored rendering was captured. Deliberately NOT keyed off
 * isDirty — an unsaved design whose rendering was captured after the edit matches the kitchen
 * exactly, so warning there made "Set As Rendering" look broken. Saving changes neither the image
 * nor the geometry and so never moves staleness. Kitchen.lastMutatedAt is session-only, so a freshly
 * loaded design with an image is never stale — that is what killed the old cross-save signature
 * comparison, which nagged across sessions and false-positived on the 2D/3D Kitchen instance split.
 * Use at every consumption gate (favorite → HubSpot, presentation) for consistent staleness.
 */
export const isRenderingStale = (params: IsRenderingStaleParams): boolean => {
	const { design } = params;
	if (!getDesignImage(design?.blueprint)) return true;

	const lastMutatedAt = design?.bpController?.kitchen?.lastMutatedAt ?? 0;
	if (!lastMutatedAt) return false;

	const capturedAt = Date.parse(design?.blueprint?.metadata?.renderingCapturedAt ?? '');
	return isNaN(capturedAt) || lastMutatedAt > capturedAt;
};
